import { useTranslation } from '@msdk/common'
import { Badge, COLORS, styled, uiTokens } from '@msdk/ui'
import React from 'react'

import { stringFormat } from '../../utils'
import { ValidationCategory, ValidationIssue } from '../ValidationPanel/validation-panel-utils'

const Label = styled('span')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${uiTokens.spacing.gap.small}px;
`

const LabelText = styled('span')`
  min-width: 0;
  overflow-wrap: break-word;
`

const IssueBadge = styled(Badge, { shouldForwardProp: (prop) => prop !== '$category' })<{
  $category: ValidationCategory
}>`
  flex-shrink: 0;

  .MuiBadge-badge {
    position: static;
    transform: none;
    background-color: ${(props) =>
      props.$category === ValidationCategory.Error ? COLORS.validation.error.main : COLORS.validation.warning.main};
    color: ${COLORS.white.main};
  }
`

interface Props {
  children: React.ReactNode
  issues: ValidationIssue[]
}

export function ValidationTabLabel({ children, issues }: Props) {
  const t = useTranslation()
  if (!issues.length) {
    return <>{children}</>
  }

  const errors = issues.filter((issue) => issue.category === ValidationCategory.Error).length
  const warnings = issues.length - errors
  const descriptions: string[] = []
  if (errors) {
    descriptions.push(
      stringFormat(
        errors === 1
          ? t('CMPSR_VALIDATION_ERROR_COUNT_SINGLE', '{0} issue')
          : t('CMPSR_VALIDATION_ERROR_COUNT', '{0} issues'),
        errors.toString(),
      ),
    )
  }
  if (warnings) {
    descriptions.push(
      stringFormat(
        warnings === 1
          ? t('CMPSR_VALIDATION_WARNING_COUNT_SINGLE', '{0} warning')
          : t('CMPSR_VALIDATION_WARNING_COUNT', '{0} warnings'),
        warnings.toString(),
      ),
    )
  }

  const description =
    descriptions.length === 1
      ? descriptions[0]
      : stringFormat(t('CMPSR_VALIDATION_MIXED_SUMMARY', '{0}, {1}'), ...descriptions)

  return (
    <Label>
      <LabelText>{children}</LabelText>
      <IssueBadge
        $category={errors ? ValidationCategory.Error : ValidationCategory.Warning}
        badgeContent={issues.length}
        max={Number.MAX_SAFE_INTEGER}
        aria-label={description}
      />
    </Label>
  )
}
